import {
  CAT_RUNNING_CROW_HEIGHT,
  CAT_RUNNING_CROW_HIGH_Y,
  CAT_RUNNING_CROW_LOW_Y,
  CAT_RUNNING_CROW_WIDTH,
  CAT_RUNNING_GROUND_OBSTACLE_SPECS,
  CAT_RUNNING_GROUND_Y,
  CAT_RUNNING_OBSTACLE_MAX_SPAWN_MS,
  CAT_RUNNING_OBSTACLE_MIN_SPAWN_MS,
  CAT_RUNNING_OBSTACLE_SPAWN_X,
  CAT_RUNNING_PHASE_2_SPEED,
  CAT_RUNNING_PHASE_3_SPEED,
  CAT_RUNNING_TEST_CROW_MODE,
  CAT_RUNNING_TEST_CROW_SPAWN_MAX_MS,
  CAT_RUNNING_TEST_CROW_SPAWN_MIN_MS,
} from '../../constants'
import { getChuruHitbox, getObstacleHitbox, isRectOverlap } from '../collision'

const getRandomBetween = (min, max, randomFn = Math.random) => min + randomFn() * (max - min)
const MAX_OBSTACLE_SPAWN_ATTEMPTS = 6

export const getRandomObstacleSpawnMs = () =>
  getRandomBetween(
    CAT_RUNNING_TEST_CROW_MODE ? CAT_RUNNING_TEST_CROW_SPAWN_MIN_MS : CAT_RUNNING_OBSTACLE_MIN_SPAWN_MS,
    CAT_RUNNING_TEST_CROW_MODE ? CAT_RUNNING_TEST_CROW_SPAWN_MAX_MS : CAT_RUNNING_OBSTACLE_MAX_SPAWN_MS,
  )

export const getRandomObstacleSpawnMsWith = (randomFn) =>
  getRandomBetween(
    CAT_RUNNING_TEST_CROW_MODE ? CAT_RUNNING_TEST_CROW_SPAWN_MIN_MS : CAT_RUNNING_OBSTACLE_MIN_SPAWN_MS,
    CAT_RUNNING_TEST_CROW_MODE ? CAT_RUNNING_TEST_CROW_SPAWN_MAX_MS : CAT_RUNNING_OBSTACLE_MAX_SPAWN_MS,
    randomFn,
  )

const getCurrentObstaclePhase = (world) => {
  if (world.speed >= CAT_RUNNING_PHASE_3_SPEED) return 3
  if (world.speed >= CAT_RUNNING_PHASE_2_SPEED) return 2
  return 1
}

const PHASE_1_POOL = ['ground-0', 'ground-0', 'ground-1']
const PHASE_2_POOL = [...PHASE_1_POOL, 'ground-2', 'crow-low']
const PHASE_3_POOL = [...PHASE_2_POOL, 'crow-high', 'ground-2']

const buildCrowObstacle = (world, y, key) => {
  const randomFn = world.randomFn ?? Math.random

  return {
    id: `${world.elapsedMs}-${key}-${randomFn().toString(36).slice(2, 8)}`,
    kind: 'crow',
    variantIndex: 0,
    frameIndex: 0,
    x: CAT_RUNNING_OBSTACLE_SPAWN_X,
    previousX: CAT_RUNNING_OBSTACLE_SPAWN_X,
    y,
    width: CAT_RUNNING_CROW_WIDTH,
    height: CAT_RUNNING_CROW_HEIGHT,
  }
}

const isObstaclePlacementBlocked = (world, obstacle) => {
  const candidateHitbox = getObstacleHitbox(obstacle)

  return (
    world.obstacles.some((activeObstacle) =>
      isRectOverlap(candidateHitbox, getObstacleHitbox(activeObstacle))) ||
    world.churus.some((churu) =>
      isRectOverlap(candidateHitbox, getChuruHitbox(churu)))
  )
}

const buildObstacleFromPoolEntry = (world, entry) => {
  if (entry === 'crow-low') {
    return buildCrowObstacle(world, CAT_RUNNING_CROW_LOW_Y, entry)
  }

  if (entry === 'crow-high') {
    return buildCrowObstacle(world, CAT_RUNNING_CROW_HIGH_Y, entry)
  }

  const specIndex = Number(entry.replace('ground-', ''))
  const nextSpec = CAT_RUNNING_GROUND_OBSTACLE_SPECS[specIndex]
  const randomFn = world.randomFn ?? Math.random

  return {
    id: `${world.elapsedMs}-${nextSpec.key}-${randomFn().toString(36).slice(2, 8)}`,
    kind: 'ground',
    variantIndex: specIndex,
    x: CAT_RUNNING_OBSTACLE_SPAWN_X,
    previousX: CAT_RUNNING_OBSTACLE_SPAWN_X,
    y: CAT_RUNNING_GROUND_Y,
    width: nextSpec.width,
    height: nextSpec.height,
  }
}

const getObstaclePool = (world) => {
  if (CAT_RUNNING_TEST_CROW_MODE) {
    return ['crow-low', 'crow-high']
  }

  const phase = getCurrentObstaclePhase(world)

  if (phase >= 3) return PHASE_3_POOL
  if (phase >= 2) return PHASE_2_POOL
  return PHASE_1_POOL
}

const pickObstaclePoolEntry = (world) => {
  const pool = getObstaclePool(world)
  const canRepeatPreviousGround = world.obstacleSpawnState.streak < 2
  const randomFn = world.randomFn ?? Math.random

  if (canRepeatPreviousGround) {
    return pool[Math.floor(randomFn() * pool.length)]
  }

  const filteredPool = pool.filter((entry) => entry !== `ground-${world.obstacleSpawnState.previousIndex}`)
  const selectablePool = filteredPool.length > 0 ? filteredPool : pool

  return selectablePool[Math.floor(randomFn() * selectablePool.length)]
}

export const spawnGroundObstacle = (world) => {
  for (let attempt = 0; attempt < MAX_OBSTACLE_SPAWN_ATTEMPTS; attempt += 1) {
    const poolEntry = pickObstaclePoolEntry(world)
    const obstacle = buildObstacleFromPoolEntry(world, poolEntry)

    if (isObstaclePlacementBlocked(world, obstacle)) {
      continue
    }

    world.obstacles.push(obstacle)

    if (obstacle.kind === 'ground') {
      world.obstacleSpawnState = {
        previousIndex: obstacle.variantIndex,
        streak: world.obstacleSpawnState.previousIndex === obstacle.variantIndex ? world.obstacleSpawnState.streak + 1 : 1,
      }
    }

    return
  }
}

export const updateGroundObstacleSpawner = (world, deltaSec, warmupDistance) => {
  if (!CAT_RUNNING_TEST_CROW_MODE && world.distance < warmupDistance) return

  world.spawnTimers.obstacleMs -= deltaSec * 1000

  while (world.spawnTimers.obstacleMs <= 0) {
    spawnGroundObstacle(world)
    world.spawnTimers.obstacleMs += getRandomObstacleSpawnMsWith(world.randomFn)
  }
}
